import { useInitSalesforceMessaging } from './use-init-salesforce-messaging.hook';
import {
  useSalesforceUserVerification,
  type SalesforceUserVerificationOptions,
} from './use-salesforce-user-verification.hook';
import { useSalesforcePrechat } from './use-salesforce-prechat-user-profile.hook';

/**
 * Options for `useSalesforceChat`.
 *
 * Currently a re-export of `SalesforceUserVerificationOptions`. Typed separately
 * so that consumers remain decoupled from internal sub-hook type names.
 */
export type UseSalesforceChatOptions = SalesforceUserVerificationOptions;

/**
 * Composite hook that bootstraps Salesforce Live Chat for authenticated users.
 *
 * Combines:
 * - `useInitSalesforceMessaging` — loads and initialises the Salesforce Messaging script
 * - `useSalesforceUserVerification` — wires up identity tokens for authenticated sessions
 * - `useSalesforcePrechat` — populates pre-chat fields with League user profile data
 *
 * @param enabled - When `false`, all sub-hooks are disabled. Defaults to `true`.
 *   Typical usage: gate on authenticated/private routes.
 * @param options - Options forwarded to `useSalesforceUserVerification`. Use `getToken`
 *   for host apps that manage their own JWT lifecycle (e.g. a cookie-to-JWT bridge)
 *   without initialising `@leagueplatform/auth`.
 */
export const useSalesforceChat = (
  enabled = true,
  options?: UseSalesforceChatOptions,
): void => {
  useInitSalesforceMessaging(enabled);
  useSalesforceUserVerification(enabled, options);
  useSalesforcePrechat(enabled);
};
